/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {EntityTreeBranchModel} from '../../../../shared/entity-tree/models/entity-tree-branch.model';
import {EntityTypeEnum} from './entity-type.enum';
import {connectionsDropZoneMessage, pathDropZoneMessage} from './dropZoneMessagesUtil';
import {EntityTreeBranchTypeEnum} from '../../../../shared/entity-tree/models/entity-tree-branch-type.enum';
import {BusinessSettingsDisplayConfigurationModel} from './models/business-settings-display-configuration.model';
import {CountryModalComponent} from '../../shared/country-modal/country-modal.component';
import {PartnerModalComponent} from '../../shared/partner-modal/partner-modal.component';
import {ShipToPartyModalComponent} from '../../shared/ship-to-party-modal/ship-to-party-modal.component';
import {SupplyingPackingPlantModalComponent} from '../../shared/supplying-packing-plant-modal/supplying-packing-plant-modal.component';
import {TransportModeModalComponent} from '../../shared/transport-mode-modal/transport-mode-modal.component';
import {TransportServiceModalComponent} from '../../shared/transport-service-modal/transport-service-modal.component';
import {LeadTimeModalComponent} from '../../shared/lead-time-modal/lead-time-modal.component';
import {ReasonModalComponent} from '../../shared/reasons-modal/reason-modal.component';
import {ClaimCodeModalComponent} from '../../shared/claim-code-modal/claim-code-modal.component';
import {DecisionCodeModalComponent} from '../../shared/decision-code-modal/decision-code-modal.component';
import {ErrorCodeModalComponent} from '../../shared/error-code-modal/error-code-modal.component';
import {WarningModalComponent} from '../../shared/warning-modal/warning-modal.component';
import configurationFunctions from './business-settings-display-configuration-functions';
import {BusinessCategoryModalComponent} from '../../shared/business-category-modal/business-category-modal.component';
import {SegmentedButtonDisplayModeEnum} from '../../../../shared/segemented-button/segmented-button-display-mode.enum';
import {TechnicianGroupModalComponent} from '../../shared/technician-group-modal/technician-group-modal.component';

export const BusinessSettingsDisplayConfiguration: BusinessSettingsDisplayConfigurationModel = {
    specificBranchOptions: [
        {
            label: EntityTreeBranchTypeEnum.SPO,
            value: EntityTreeBranchTypeEnum.SPO,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            isSelected: true
        },
        {
            label: EntityTreeBranchTypeEnum.CLAIM,
            value: EntityTreeBranchTypeEnum.CLAIM,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            isSelected: false
        },
        {
            label: EntityTreeBranchTypeEnum.PNC,
            value: EntityTreeBranchTypeEnum.PNC,
            displayMode: SegmentedButtonDisplayModeEnum.BASE,
            isSelected: false
        }
    ],
    commonBranch: new Map<EntityTypeEnum, EntityTreeBranchModel>([
        [EntityTypeEnum.COUNTRIES, {
            originalPosition: 1,
            isRoot: true,
            name: EntityTypeEnum.COUNTRIES,
            entityCode: 'countries',
            nameLabelText: 'country/region',
            fullNameLabelText: 'country/region',
            hasPath: true,
            hasConnections: false,
            pathTo: [{type: EntityTypeEnum.PARTNERS, active: true, hasSingleConnectedChild: false}],
            pool: [],
            dropZoneMessages: {
                path: connectionsDropZoneMessage('country/region')
            },
            hasDisplayCode: true,
            displayCodeFunction: configurationFunctions.countries.countriesDisplayCode,
            childrenCodeFunction: configurationFunctions.countries.countriesChildrenCode,
            hasMultipleConnectedParents: false,
            modalType: CountryModalComponent,
            mapOriginalValueFunction: configurationFunctions.countries.countriesMapOriginalValue,
            skippable: false
        }],
        [EntityTypeEnum.PARTNERS, {
            originalPosition: 2,
            name: EntityTypeEnum.PARTNERS,
            entityCode: 'partners',
            nameLabelText: 'partner',
            fullNameLabelText: 'partner',
            hasPath: true,
            hasConnections: true,
            pathTo: [{
                type: EntityTypeEnum.SHIP_TO_PARTIES,
                active: true,
                hasSingleConnectedChild: false
            }, {
                type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS,
                active: true,
                hasSingleConnectedChild: false
            }],
            connections: [],
            pool: [],
            dropZoneMessages: {
                path: pathDropZoneMessage('partner'),
                connections: connectionsDropZoneMessage('partner')
            },
            pathFrom: [{type: EntityTypeEnum.COUNTRIES, active: true}],
            hasDisplayCode: true,
            childrenCodeFunction: configurationFunctions.partners.partnersChildrenCodeFunction,
            hasMultipleConnectedParents: false,
            modalType: PartnerModalComponent,
            mapOriginalValueFunction: configurationFunctions.partners.partnersMapOriginalValue,
            skippable: false
        }],
        [EntityTypeEnum.SHIP_TO_PARTIES, {
            originalPosition: 3,
            name: EntityTypeEnum.SHIP_TO_PARTIES,
            entityCode: 'ship-to-parties',
            nameLabelText: 'party',
            fullNameLabelText: 'ship to party',
            hasPath: false,
            hasConnections: true,
            connections: [],
            pool: [],
            pathFrom: [{type: EntityTypeEnum.PARTNERS, active: true}],
            pathTo: [{type: EntityTypeEnum.TECHNICIANS_GROUPS, active: false, hasSingleConnectedChild: false}],
            dropZoneMessages: {
                connections: connectionsDropZoneMessage('party'),
                path: pathDropZoneMessage('party')
            },
            hasMultipleConnectedParents: false,
            modalType: ShipToPartyModalComponent,
            hasDisplayCode: true,
            mapOriginalValueFunction: configurationFunctions.shipToParties.shipToPartiesMapOriginalValue,
            skippable: false,
            invalidElementWarning: 'Inactive parties do not appear as an option for the the partners SPOs',
            isFinal: true,
            representParent: true,
            isBranchFinal: true
        }],
        [EntityTypeEnum.SUPPLYING_PACKING_PLANTS, {
            originalPosition: 4,
            name: EntityTypeEnum.SUPPLYING_PACKING_PLANTS,
            entityCode: 'supplying-packing-plants',
            nameLabelText: 'plant',
            fullNameLabelText: 'supplying packing plant',
            hasPath: true,
            hasConnections: true,
            connections: [],
            pool: [],
            pathFrom: [{type: EntityTypeEnum.PARTNERS, active: true}],
            pathTo: [{
                type: EntityTypeEnum.TRANSPORT_MODES,
                active: false,
                hasSingleConnectedChild: false
            }, {
                type: EntityTypeEnum.CLAIM_CODES,
                active: false,
                hasSingleConnectedChild: false
            }],
            dropZoneMessages: {
                path: pathDropZoneMessage('plant'),
                connections: connectionsDropZoneMessage('plant')
            },
            hasMultipleConnectedParents: true,
            modalType: SupplyingPackingPlantModalComponent,
            hasDisplayCode: true,
            mapOriginalValueFunction: configurationFunctions.supplyingPackingPlants.supplyingPackingPlantsMapOriginalValue,
            skippable: false,
            isBranchFinal: true
        }]
    ]),
    specificBranches: new Map<EntityTreeBranchTypeEnum, Map<EntityTypeEnum, EntityTreeBranchModel>>([
        [EntityTreeBranchTypeEnum.SPO, new Map<EntityTypeEnum, EntityTreeBranchModel>([
            [EntityTypeEnum.TRANSPORT_MODES, {
                originalPosition: 1,
                name: EntityTypeEnum.TRANSPORT_MODES,
                entityCode: 'transport-modes',
                nameLabelText: 'mode',
                fullNameLabelText: 'transport mode',
                hasPath: true,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS, active: true}],
                pathTo: [{
                    type: EntityTypeEnum.TRANSPORT_SERVICES,
                    active: false,
                    excludes: EntityTypeEnum.LEAD_TIMES,
                    hasSingleConnectedChild: false
                }, {
                    type: EntityTypeEnum.LEAD_TIMES,
                    active: true,
                    excludes: EntityTypeEnum.TRANSPORT_SERVICES,
                    hasSingleConnectedChild: true
                }],
                dropZoneMessages: {
                    path: pathDropZoneMessage('transport mode'),
                    connections: connectionsDropZoneMessage('transport mode')
                },
                hasMultipleConnectedParents: true,
                modalType: TransportModeModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.transportModes.transportModesMapOriginalValue,
                skippable: false,
                isSpecificBranchRoot: true
            }],
            [EntityTypeEnum.TRANSPORT_SERVICES, {
                originalPosition: 2,
                name: EntityTypeEnum.TRANSPORT_SERVICES,
                entityCode: 'transport-services',
                nameLabelText: 'service',
                fullNameLabelText: 'transport service',
                hasPath: true,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{type: EntityTypeEnum.TRANSPORT_MODES, active: false}
                ],
                pathTo: [{type: EntityTypeEnum.LEAD_TIMES, active: true, hasSingleConnectedChild: true}],
                dropZoneMessages: {
                    path: pathDropZoneMessage('transport service'),
                    connections: connectionsDropZoneMessage('transport service')
                },
                hasMultipleConnectedParents: true,
                iconCode: 'iwp-icon-csc_request',
                hasIconFunction: configurationFunctions.transportServices.transportServiceHasIcon,
                modalType: TransportServiceModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.transportServices.transportServicesMapOriginalValue,
                skippable: true,
                hasManualSorting: true
            }],
            [EntityTypeEnum.LEAD_TIMES, {
                originalPosition: 3,
                name: EntityTypeEnum.LEAD_TIMES,
                entityCode: 'lead-time',
                nameLabelText: 'lead time',
                fullNameLabelText: 'lead time',
                hasPath: true,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{
                    type: EntityTypeEnum.TRANSPORT_SERVICES,
                    active: false
                }, {
                    type: EntityTypeEnum.TRANSPORT_MODES,
                    active: true
                }],
                pathTo: [{type: EntityTypeEnum.WARNINGS, active: false, hasSingleConnectedChild: true}],
                dropZoneMessages: {
                    path: pathDropZoneMessage('lead time'),
                    connections: connectionsDropZoneMessage('lead time')
                },
                hasDisplayCode: true,
                displayModalNameFunction: configurationFunctions.leadTimes.leadTimeDisplayModalName,
                displayNameFunction: configurationFunctions.leadTimes.leadTimesDisplayName,
                displayCodeFunction: configurationFunctions.leadTimes.leadTimesDisplayCode,
                sortingValueFunction: configurationFunctions.leadTimes.leadTimeSortingValue,
                breadcrumbLabelFunction: configurationFunctions.leadTimes.leadTimeDisplayModalName,
                hasMultipleConnectedParents: true,
                modalType: LeadTimeModalComponent,
                mapOriginalValueFunction: configurationFunctions.leadTimes.leadTimesMapOriginalValue,
                skippable: false,
                isFinal: true
            }],
            [EntityTypeEnum.WARNINGS, {
                originalPosition: 4,
                name: EntityTypeEnum.WARNINGS,
                entityCode: 'warnings',
                nameLabelText: 'warning',
                fullNameLabelText: 'warning',
                hasPath: false,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{
                    type: EntityTypeEnum.LEAD_TIMES,
                    active: false
                }],
                dropZoneMessages: {
                    connections: connectionsDropZoneMessage('warning')
                },
                hasMultipleConnectedParents: true,
                hasDisplayCode: false,
                displayModalNameFunction: configurationFunctions.warnings.warningsDisplayModalName,
                displayNameFunction: configurationFunctions.warnings.warningsDisplayName,
                sortingValueFunction: configurationFunctions.warnings.warningsSortingValue,
                modalType: WarningModalComponent,
                mapOriginalValueFunction: configurationFunctions.warnings.warningsMapOriginalValue,
                breadcrumbLabelFunction: configurationFunctions.warnings.warningsDisplayName,
                skippable: true,
                isFinal: true
            }],
            [EntityTypeEnum.REASONS, {
                originalPosition: 5,
                name: EntityTypeEnum.REASONS,
                entityCode: 'reasons',
                nameLabelText: 'reason',
                fullNameLabelText: 'reason',
                hasPath: false,
                hasConnections: false,
                connections: [],
                pool: [],
                pathFrom: null,
                pathTo: [],
                dropZoneMessages: {
                    path: null,
                    connections: null
                },
                hasMultipleConnectedParents: false,
                modalType: ReasonModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.reasons.reasonsMapOriginalValue,
                skippable: false
            }]
        ])],
        [EntityTreeBranchTypeEnum.CLAIM, new Map<EntityTypeEnum, EntityTreeBranchModel>([
            [EntityTypeEnum.CLAIM_CODES, {
                originalPosition: 1,
                name: EntityTypeEnum.CLAIM_CODES,
                entityCode: 'claim-codes',
                nameLabelText: 'claim code',
                fullNameLabelText: 'claim code',
                hasPath: true,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{type: EntityTypeEnum.SUPPLYING_PACKING_PLANTS, active: true}],
                pathTo: [{type: EntityTypeEnum.DECISION_CODES, active: true, hasSingleConnectedChild: false}],
                dropZoneMessages: {
                    path: pathDropZoneMessage('claim code'),
                    connections: connectionsDropZoneMessage('claim code')
                },
                hasMultipleConnectedParents: true,
                iconCode: 'iwp-icon-ci_paperclip',
                hasIconFunction: configurationFunctions.claimCodes.claimCodesHasIcon,
                modalType: ClaimCodeModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.claimCodes.claimCodesMapOriginalValue,
                skippable: false,
                isSpecificBranchRoot: true
            }],
            [EntityTypeEnum.DECISION_CODES, {
                originalPosition: 2,
                name: EntityTypeEnum.DECISION_CODES,
                entityCode: 'decision-codes',
                nameLabelText: 'decision',
                fullNameLabelText: 'decision',
                hasPath: false,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{type: EntityTypeEnum.CLAIM_CODES, active: true}],
                pathTo: [],
                dropZoneMessages: {
                    connections: connectionsDropZoneMessage('decision code')
                },
                iconCode: 'iwp-icon-gen_time',
                hasIconFunction: configurationFunctions.decisionCodes.decisionCodesHasIcon,
                hasMultipleConnectedParents: true,
                modalType: DecisionCodeModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.decisionCodes.decisionCodesMapOriginalValue,
                updatePoolItemsAfterElementChangeFunction: configurationFunctions.decisionCodes.decisionCodeUpdatePoolItems,
                updatePoolItemsAfterElementDeleteFunction: configurationFunctions.decisionCodes.removeCodeInExcludedDecisions,
                skippable: false,
                isFinal: true
            }],
            [EntityTypeEnum.ERROR_CODES, {
                originalPosition: 3,
                name: EntityTypeEnum.ERROR_CODES,
                entityCode: 'error-codes',
                nameLabelText: 'error code',
                fullNameLabelText: 'error code',
                hasPath: false,
                hasConnections: false,
                connections: [],
                pool: [],
                pathFrom: null,
                pathTo: [],
                dropZoneMessages: {
                    path: null,
                    connections: null
                },
                modalType: ErrorCodeModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.errorCodes.errorCodesMapOriginalValue,
                hasMultipleConnectedParents: false,
                skippable: false
            }],
            [EntityTypeEnum.BI_CATEGORIES, {
                originalPosition: 4,
                name: EntityTypeEnum.BI_CATEGORIES,
                entityCode: 'bi-categories',
                nameLabelText: 'BI category',
                fullNameLabelText: 'BI category',
                hasPath: false,
                hasConnections: false,
                connections: [],
                pool: [],
                pathFrom: null,
                pathTo: [],
                dropZoneMessages: {
                    path: null,
                    connections: null
                },
                modalType: BusinessCategoryModalComponent,
                hasDisplayCode: true,
                mapOriginalValueFunction: configurationFunctions.businessCategories.biCategoryMapOriginalValue,
                sortingValueFunction: configurationFunctions.businessCategories.biCategorySortingAndDisplayCode,
                displayCodeFunction: configurationFunctions.businessCategories.biCategorySortingAndDisplayCode,
                hasMultipleConnectedParents: false,
                skippable: false,
                hasManualSorting: true
            }]
        ])],
        [EntityTreeBranchTypeEnum.PNC, new Map<EntityTypeEnum, EntityTreeBranchModel>([
            [EntityTypeEnum.TECHNICIANS_GROUPS, {
                originalPosition: 1,
                name: EntityTypeEnum.TECHNICIANS_GROUPS,
                entityCode: 'technicians-groups',
                nameLabelText: 'group',
                fullNameLabelText: 'technician group',
                hasPath: false,
                hasConnections: true,
                connections: [],
                pool: [],
                pathFrom: [{type: EntityTypeEnum.SHIP_TO_PARTIES, active: true}],
                pathTo: [],
                dropZoneMessages: {
                    connections: connectionsDropZoneMessage('group')
                },
                modalType: TechnicianGroupModalComponent,
                hasMultipleConnectedParents: true,
                mapOriginalValueFunction: configurationFunctions.technicianGroups.technicianGroupMapOriginalValue,
                displayCodeFunction: configurationFunctions.technicianGroups.technicianGroupsDisplayCode,
                updatePoolItemsAfterAddElementToConnections: configurationFunctions.technicianGroups.technicianGroupsAfterAddToConnections,
                updatePoolItemsAfterRemoveElementFromConnections: configurationFunctions.technicianGroups
                        .technicianGroupsAfterRemoveFromConnections,
                breadcrumbLabelFunction: configurationFunctions.technicianGroups.techniciansGroupsBreadcrumbLabel,
                updatePoolNewItemContextMenuActions: configurationFunctions.technicianGroups.techniciansGroupPoolNewItemContextMenuActions,
                updatePoolItemsContextMenuActions: configurationFunctions.technicianGroups.techniciansGroupsUpdatePoolContextMenuActions,
                hasDisplayCode: true,
                skippable: false,
                isSpecificBranchRoot: true
            }]
        ])]
    ])
};
